<script setup>

import { computed, inject, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { DateTime, Settings } from 'luxon';
import Papa from "papaparse";
import { saveAs } from 'file-saver';
import { useCommunitiesStore } from "@/stores/communitiesStore";
import { useCommunityCompaniesStore } from "@/stores/communityCompaniesStore";
import { extractDateRangesFromDateArray, formatDate, formatDateTime } from "../lib/es-bill-shared-js/misc/timeTools";
import { useDataAnalysisStore } from "@/stores/dataAnaysisStore";
import DsoTools from "@/lib/es-bill-shared-js/misc/DsoTools";
import MeterCodes from "@/lib/es-bill-shared-js/MeterCodes";
import { DateRangePicker, PrimaryButton } from '@es-bill/es-bill-common-components';
import { resolveComboboxItem, isComboboxItem } from "@/utils/comboboxUtils";

const $log = inject('log');
const { t } = useI18n();
Settings.defaultZone = 'Europe/Vienna'; // might be redundant

const communitiesStore = useCommunitiesStore();
const communityCompaniesStore = useCommunityCompaniesStore();
const dataAnalysisStore = useDataAnalysisStore();


const communityAvailable = computed(() => {
  const communities = communitiesStore.allCommunities;

  return communities.map(c => {
    return {
      title: c.name,
      props: { subtitle: c.ecid },
      ecn: c.ecn,
      ecid: c.ecid,
      community: c,
      communityCompany: communityCompaniesStore.allCommunityCompanies.find(cc => cc.ecn === c.ecn),
    };
  })
})

const AnalysisNone = "";
const AnalysisCompleteness = "COMPLETENESS";
const AnalysisOverviewBadMps = "L3_OVERVIEW";
const AnalysisDetailBadMps = "L3_DETAIL";
const AnalysisWrongQh = "WRONG_QH";

const analysisAvailable = ref([
  AnalysisCompleteness,
  AnalysisOverviewBadMps,
  AnalysisDetailBadMps,
  AnalysisWrongQh,
])


const selection = ref({
  analysisType: AnalysisNone,

  communityEntry: null,
  dateRange: [],
  details: false,
  onlyBillingRelevant: false,

  // post analysis
  dso: null,
});

watch(() => selection.value.communityEntry, value => {
  selection.value.communityEntry = resolveComboboxItem(value, communityAvailable.value, ['ecid', 'title']);
});

const tableItems = computed(() => {
  if(analysisResultType.value===AnalysisCompleteness)
    return completenessResult.value;
  if(analysisResultType.value===AnalysisOverviewBadMps)
    return overviewResult.value;
  if(analysisResultType.value===AnalysisDetailBadMps)
    return detailResultFilteredForBilling.value;
  if(analysisResultType.value===AnalysisWrongQh)
    return qhResult.value;
  return [];
})
const tableHeader = computed(() => {
  if(analysisResultType.value===AnalysisCompleteness)
    return tableCompleteness.value;
  if(analysisResultType.value===AnalysisOverviewBadMps)
    return tableOverviewHeader.value;
  if(analysisResultType.value===AnalysisDetailBadMps)
    return tableDetailHeader.value;
  if(analysisResultType.value===AnalysisWrongQh)
    return tableWrongQhs.value;
  return [];
})
const tableSort = computed(() => {
  // if(analysisResultType.value===AnalysisCompleteness)
  //   return [{ key: 'meteringPoint', order: 'asc' },{ key: 'date', order: 'asc' }];
  // if(analysisResultType.value===AnalysisOverviewBadMps)
  //   return [{ key: 'meteringPoint', order: 'asc' },{ key: 'date', order: 'asc' }];
  // if(analysisResultType.value===AnalysisDetailBadMps)
  //   return [{ key: 'meteringPoint', order: 'asc' },{ key: 'date', order: 'asc' }];
  // if(analysisResultType.value===AnalysisWrongQh)
  //   return [{ key: 'meteringPoint', order: 'asc' },{ key: 'date', order: 'asc' }];
  return [{ key: 'date', order: 'asc' }];
})
const analysisName = computed(() => {
  if(analysisResultType.value===AnalysisCompleteness)
    return "Auswertung nach vorhandenen Energiedaten";
  if(analysisResultType.value===AnalysisOverviewBadMps)
    return "Auswertung nach nicht belastbaren Zählpunkten/Tagen";
  if(analysisResultType.value===AnalysisDetailBadMps)
    return "Auswertung nach nicht belastbaren Zeitreihen"
  if(analysisResultType.value===AnalysisWrongQh)
    return "Auswertung nach fehlenden Viertelstunden"
  return "Auswertung";
})
const analysisFileName = computed(() => {
  if(analysisResultType.value===AnalysisCompleteness)
    return "Auswertung-Vollständigkeit";
  if(analysisResultType.value===AnalysisOverviewBadMps)
    return "Auswertung-nicht-belastbare-Tage";
  if(analysisResultType.value===AnalysisDetailBadMps)
    return "Detailauswertung-nicht-belastbare-Tage";
  if(analysisResultType.value===AnalysisWrongQh)
    return "Detailauswertung-fehlenden-Viertelstunden"
  return "Auswertung";
})


const requestParameters = computed(() => {
  let dr = [];
  if(selection.value.dateRange.length === 1) {
    const dateStart = DateTime.fromISO(selection.value.dateRange[0]);
    $log.warn(dateStart)
    dr = [dateStart.toJSDate(), dateStart.toJSDate()];
  }
  else if(selection.value.dateRange.length > 1) {
    const dateStart = DateTime.fromISO(selection.value.dateRange[0]);
    let dateEnd = DateTime.fromISO(selection.value.dateRange[
      selection.value.dateRange.length-1]);

    dateEnd=dateEnd.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    dr = [dateStart.toJSDate(), dateEnd.toJSDate()]
  }

  return {
    community: selection.value.communityEntry?.community || null,
    dateRange: dr,
    canBeRequested: dr.length && isComboboxItem(selection.value.communityEntry) && selection.value.analysisType !== "",
    analysisType: selection.value.analysisType,
    details: selection.value.details
  }
})


const analysisRunning = ref(false);
const analysisResultType = ref(AnalysisNone)

async function startAnalysis() {
  analysisRunning.value = true;

  if(requestParameters.value.analysisType === AnalysisCompleteness)
    await getAnalysisCompleteness();
  if(requestParameters.value.analysisType === AnalysisOverviewBadMps)
    await getAnalysisBadOverview();
  else if(requestParameters.value.analysisType === AnalysisDetailBadMps)
    await getAnalysisBadDetails();
  else if(requestParameters.value.analysisType === AnalysisWrongQh)
    await getAnalysisWrongQh();

  analysisRunning.value = false;
}

const completenessResult = ref([]);
async function getAnalysisCompleteness() {
  completenessResult.value = [];
  overviewResult.value = [];
  detailResult.value = [];
  qhResult.value = [];
  dsoList.value = [];
  selection.value.dso = null;
  jsonResult.value = "";

  const c = requestParameters.value.community;

  const wrongMcs = await dataAnalysisStore.getIncorrectMeterCodes(
    requestParameters.value.community.ecn,
    requestParameters.value.community.ecid,
    requestParameters.value.dateRange[0],
    requestParameters.value.dateRange[1],
    requestParameters.value.community.ecn.startsWith("EP") // current CM hack
  );

  analysisResultType.value = AnalysisCompleteness;
  if(wrongMcs.length === 0) {
    completenessResult.value = [{
      meteringPoint: "keine Fehler gefunden",
    }]
    return;
  }

  completenessResult.value = wrongMcs.map(e => {

    const ecnDso = DsoTools.detectDso(c,e.meteringPoint.substring(0,8));
    if(!dsoList.value.find(e => e.ecnDso === ecnDso)) {
      dsoList.value.push({
        ecnDso: ecnDso,
        name: DsoTools.getDsoByEcn(ecnDso).title,
        props: { subtitle: ecnDso },
      })
    }

    return {
      ...e,
      ecnDso: ecnDso,
      dateString: formatDate(e.date),
    }
  });
}

const overviewResult = ref([])
async function getAnalysisBadOverview() {
  completenessResult.value = [];
  overviewResult.value = [];
  detailResult.value = [];
  qhResult.value = [];
  dsoList.value = [];
  selection.value.dso = null;
  jsonResult.value = "";

  const c = requestParameters.value.community;

  const badMpDays = await dataAnalysisStore.getUncleanMps(
    requestParameters.value.community.ecn,
    requestParameters.value.community.ecid,
    requestParameters.value.dateRange[0],
    requestParameters.value.dateRange[1],
  );

  analysisResultType.value = AnalysisOverviewBadMps;
  if(badMpDays.length === 0) {
    overviewResult.value = [{
      meteringPoint: "keine Fehler gefunden",
    }]
    return;
  }

  overviewResult.value = badMpDays.map(e => {

    const ecnDso = DsoTools.detectDso(c,e.meteringPoint.substring(0,8));
    if(!dsoList.value.find(e => e.ecnDso === ecnDso)) {
      dsoList.value.push({
        ecnDso: ecnDso,
        name: DsoTools.getDsoByEcn(ecnDso).title,
        props: { subtitle: ecnDso },
      })
    }

    return {
      ...e,
      ecnDso: ecnDso,
      dateString: formatDate(e.date),
    }
  });
}

const detailResult = ref([])

const detailResultFilteredForBilling = computed(() => {
  if(!selection.value.onlyBillingRelevant)
    return detailResult.value;

  return detailResult.value.filter(e => {
    /// https://es-bill.atlassian.net/wiki/spaces/ESBILL/pages/39518209/ConsumptionRecord#Verwendung-%C3%BCber-Versionen
    /// only correct for "slot 3"
    if(e.meterCode === MeterCodes.memberShareEffective)
      return true
    if(e.meterCode === MeterCodes.memberProductionForCommunityMft)
      return true
    if(e.meterCode === MeterCodes.producerProductionRemainderMft)
      return true

    return false;
  });
})

async function getAnalysisBadDetails() {
  completenessResult.value = [];
  overviewResult.value = [];
  detailResult.value = [];
  qhResult.value = [];
  dsoList.value = [];
  selection.value.dso = null;
  jsonResult.value = "";

  const c = requestParameters.value.community;

  const badMpDays = await dataAnalysisStore.getUncleanDataEntriesForCommunity(
    requestParameters.value.community.ecn,
    requestParameters.value.community.ecid,
    requestParameters.value.dateRange[0],
    requestParameters.value.dateRange[1],
  );

  analysisResultType.value = AnalysisDetailBadMps;
  if(badMpDays.length === 0) {
    detailResult.value = [{
      meteringPoint: "keine Fehler gefunden",
    }]
    return;
  }

  detailResult.value = badMpDays.map(e => {

    const ecnDso = DsoTools.detectDso(c,e.meteringPoint.substring(0,8));
    if(!dsoList.value.find(e => e.ecnDso === ecnDso)) {
      dsoList.value.push({
        ecnDso: ecnDso,
        name: DsoTools.getDsoByEcn(ecnDso).title,
        props: { subtitle: ecnDso },
      })
    }

    return {
      ...e,
      ecnDso: ecnDso,
      dateString: formatDate(e.date),
      dateLastUpdateString: formatDate(e.dateLastUpdate),
    }
  });
}


const qhResult = ref([])
async function getAnalysisWrongQh() {
  completenessResult.value = [];
  overviewResult.value = [];
  detailResult.value = [];
  qhResult.value = [];
  dsoList.value = [];
  selection.value.dso = null;
  jsonResult.value = "";

  const c = requestParameters.value.community;

  const wrongQhResult = await dataAnalysisStore.getIncorrectCountofQh(
    requestParameters.value.community.ecn,
    requestParameters.value.community.ecid,
    requestParameters.value.dateRange[0],
    requestParameters.value.dateRange[1],
  );

  $log.debug("wrongQhResult", wrongQhResult);

  analysisResultType.value = AnalysisWrongQh;
  if(wrongQhResult.length === 0) {
    qhResult.value = [{
      meteringPoint: "keine Fehler gefunden",
    }]
    return;
  }

  qhResult.value = wrongQhResult.map(e => {

    const ecnDso = DsoTools.detectDso(c,e.meteringPoint.substring(0,8));
    if(!dsoList.value.find(e => e.ecnDso === ecnDso)) {
      dsoList.value.push({
        ecnDso: ecnDso,
        name: DsoTools.getDsoByEcn(ecnDso).title,
        props: { subtitle: ecnDso },
      })
    }

    return {
      ...e,
      ecnDso: ecnDso,
      dateString: formatDate(e.date),
      dateLastUpdateString: formatDate(e.dateLastUpdate),
    }
  });
}

const tableCompleteness = computed(() => [
  { title: 'Zählpunktnummer', value: 'meteringPoint', sortable: true, },
  { title: 'Datum', value: 'date', sortable: true,  },
  { title: 'fehlend', value: 'meterCodesMissing', sortable: true,  },
  { title: 'zu viel', value: 'meterCodesSurplus', sortable: true,  },
  { title: 'VNB', value: 'ecnDso', sortable: true,
    align: requestParameters.value?.community?.communityType!=="16b"?' d-none':'',},
  { title: 'Datum', value: 'dateString', align: ' d-none',  },
]);
const tableOverviewHeader = computed(() => [
  { title: 'Zählpunktnummer', value: 'meteringPoint', sortable: true, },
  { title: 'Datum', value: 'date', sortable: true,  },
  { title: 'VNB', value: 'ecnDso', sortable: true,
    align: requestParameters.value?.community?.communityType!=="16b"?' d-none':'',},
  { title: 'Datum', value: 'dateString', align: ' d-none',  },
]);
const tableDetailHeader = computed(() => [
  { title: 'Zählpunktnummer', value: 'meteringPoint', sortable: true,  },
  { title: 'Datum', value: 'date', sortable: true,  },
  { title: 'Zählwerkscode', value: 'meterCode', sortable: true,  },
  { title: 'Methode der Messung', value: 'sumEntry.meteringMethod', sortable: true,  },
  { title: 'letztes Update', value: 'dateLastUpdate', sortable: true,  },
  { title: 'VNB', value: 'ecnDso', sortable: true,
    align: requestParameters.value?.community?.communityType!=="16b"?' d-none':'',},
  { title: 'Datum', value: 'dateString', align: ' d-none',  },
  { title: 'Datum', value: 'dateLastUpdateString', align: ' d-none',  },
]);
const tableWrongQhs = computed(() => [
  { title: 'Zählpunktnummer', value: 'meteringPoint', sortable: true, },
  { title: 'Datum', value: 'date', sortable: true,  },
  { title: 'VNB', value: 'ecnDso', sortable: true,
    align: requestParameters.value?.community?.communityType!=="16b"?' d-none':'',},
  { title: 'Datum', value: 'dateString', align: ' d-none',  },
  { title: 'QH# soll', value: 'qhShould', sortable: true,  },
  { title: 'QH+ ist', value: 'qh', sortable: true,  },
]);

const searchString = ref("");
const dsoList = ref([]);


function downloadCsv() {
  if(selection.value.dso === null)
    return;

  const entriesToExport = tableItems.value.
    filter(e => e.ecnDso === selection.value.dso.ecnDso).
    map(e=>{
      return {
        'Zählpunktnummer': e.meteringPoint,
        'Datum': formatDate(e.date),
        'Zählwerkscode': e.meterCode,
        'Methode der Messung': e.sumEntry?.meteringMethod || "",
        'letztes Update': formatDateTime(e.dateLastUpdate),
        'VNB': e.ecnDso,
        'fehlend': e.meterCodesMissing?.join(', '),
        'zu viel': e.meterCodesSurplus?.join(', '),
      }
  });

  $log.warn(tableItems.value)
  $log.warn(entriesToExport)


  const csvString = Papa.unparse({
    "fields": tableHeader.value.filter(e=>!e.align).map(e => e.title),
    "data": entriesToExport,
    }, {
      quotes: false, //or array of booleans
      quoteChar: '"',
      escapeChar: '"',
      delimiter: ",",
      header: true,
      newline: "\r\n",
      skipEmptyLines: false, //other option is 'greedy', meaning skip delimiters, quotes, and whitespace.
      columns: null //or array of strings
    });

  const fileNameParts = [
    requestParameters.value.community.ecn,
    requestParameters.value.community.ecid,
    analysisFileName.value,
    selection.value.dso.ecnDso,
    formatDate(requestParameters.value.dateRange[0]),
    "bis",
    formatDate(requestParameters.value.dateRange[1]),
    "vom",
    formatDate(new Date()),
  ];

  const downloadData = new Blob([csvString], { type: "text/csv;charset=utf-8" });
  saveAs(downloadData,fileNameParts.join('_')+".csv");
}

const jsonResult = ref("")

function generateJson() {
  const analysisResult = tableItems.value.
    filter(e => e.ecnDso === selection.value.dso.ecnDso);
  let mpToDateArray = {};
  //
  // $log.warn("analysisResult",analysisResult)

  // reorganize by MP and date
  for(const entry of analysisResult) {

    const mp = entry.meteringPoint;
    if(!mpToDateArray[mp])
      mpToDateArray[mp] = [];

    if(!mpToDateArray[mp].find(date => date === entry.date))
      mpToDateArray[mp].push(entry.date)
  }

  // $log.warn("mpToDateArray",mpToDateArray)

  let stuffToRequest = [];

  for(const mp in mpToDateArray) {
    const ranges = extractDateRangesFromDateArray(mpToDateArray[mp]);
    // $log.warn("mp/mpToDateArray[mp]/ranges",mp,mpToDateArray[mp],ranges)

    for(const range of ranges) {
      stuffToRequest.push({
        ecn: requestParameters.value.community.ecn,
        ecid: requestParameters.value.community.ecid,
        meteringPoint: mp,
        dateFrom: range[0],
        dateTo: range[1],
      });
    }
  }

  jsonResult.value = JSON.stringify(stuffToRequest, null, 2);
}


</script>
<script>
export default {
  name: 'ViewCommunityAnalysisBadMeteringPoints',
}
</script>


<template>
  <v-container class="containerBase pa-0">

    <v-container class="containerStandard">

      <p class="textHeadline">{{ $t('analysisBadMps') }}</p>
      Bla bla bla MR. Freeman<br/>
      <br/>

      <v-combobox
                  max-width="228px"
                  bg-color="white"
                  :class="{'mr-0': $vuetify.display.xs}"
                  :placeholder="t('community')"
                  v-model="selection.communityEntry"
                  :items="communityAvailable"
                  :filter-keys="['title', 'value']"
                  density="comfortable"
                  variant="outlined"
                  item-value="ecid"
                  rounded="2"
                  hide-details
                  clearable
      ></v-combobox>
      <v-combobox
        max-width="228px"
        bg-color="white"
        :class="{'mr-0': $vuetify.display.xs}"
        :placeholder="t('analysis')"
        v-model="selection.analysisType"
        :items="analysisAvailable"
        density="comfortable"
        variant="outlined"
        rounded="2"
        hide-details
        clearable
      ></v-combobox>

      <div class="w-100 mr-md-3 mb-4">
        <DateRangePicker
          :dateRange="selection.dateRange"
          :placeholder="t('dateRange')"
          @update:dateRange="(val) => selection.dateRange = val"
        />
      </div>


<!--      <v-checkbox-->
<!--        density="compact"-->
<!--        v-model="selection.details"-->
<!--        :label="t('detailInformation')"-->
<!--        hide-details-->
<!--        max-width="200px"-->
<!--        class="mb-4 align-checkbox-start"-->
<!--      />-->

      Gemeinschaft: {{ requestParameters.community?.name}}<br/>
      ECID: {{ requestParameters.community?.ecid}}<br/>
      DateRange: {{ formatDate(requestParameters.dateRange[0]) }} bis inkl.
        {{ formatDate(requestParameters.dateRange[1]) }}<br/>
      Analyse: {{ requestParameters.analysisType }}<br/>
      Hint CM: Completeness Check is CM-aware and will be invoked acordingly.<br/>
      <br/>


      <PrimaryButton
        :disabled="!requestParameters.canBeRequested && !analysisRunning"
        @click="startAnalysis"
        :text="$t('startAnalysis')"
      />
      <br/><br/>
      <v-row v-if="analysisRunning">
        <v-progress-circular class="mx-auto" color="blue-lighten-3" indeterminate></v-progress-circular>
      </v-row>

      <div v-if="selection.analysisType !== AnalysisNone"><br/>
      <v-text-field
          label="Suche"
          placeholder="Tabelleninhalt"
          type="text"
          clearable
          v-model="searchString"
          variant="outlined"
        ></v-text-field>

        <v-checkbox v-if="analysisResultType === AnalysisDetailBadMps"
          density="compact"
          v-model="selection.onlyBillingRelevant"
          :label="t('onlyBillingRelevant')"
          hint="only valid for consumption dates >= 8.4.2024"
          persistent-hint
          max-width="200px"
          class="mb-4 align-checkbox-start"
        />

      <v-data-table
        :sort-by="tableSort"
        :headers="tableHeader"
        :items="tableItems"
        :search="searchString"
      >

        <!-- Custom slots for status column -->
        <template v-slot:[`item.date`]="{ item }">
          <span>{{ formatDate(item.date) }}</span>
        </template>
        <template v-slot:[`item.dateLastUpdate`]="{ item }">
          <span>{{ formatDateTime(item.dateLastUpdate) }}</span>
        </template>


        <template v-slot:[`item.meterCodesMissing`]="{ item }">
          <span>{{ item.meterCodesMissing?.join(', ') }}</span>
        </template>

        <template v-slot:[`item.meterCodesSurplus`]="{ item }">
          <span>{{ item.meterCodesSurplus?.join(', ') }}</span>
        </template>


      </v-data-table>

      <v-combobox
        max-width="400px"
        bg-color="white"
        :class="{'mr-0': $vuetify.display.xs}"
        :placeholder="t('dso')"
        v-model="selection.dso"
        :items="dsoList"
        :filter-keys="['name', 'ecnDso']"
        density="comfortable"
        variant="outlined"
        item-value="ecnDso"
        item-title="name"
        rounded="2"
        hide-details
        clearable
      ></v-combobox>

      <!-- Selected Dso: {{ selection.dso?.ecnDso }} <br>-->
        <PrimaryButton
          :disabled="selection.dso === null"
          @click="downloadCsv"
          :text="$t('csv')"
        />

        <PrimaryButton
            :disabled="selection.dso === null"
            @click="generateJson"
            :text="$t('json')"
        />
        <v-textarea
            @focus="$event.target.select()"
            readonly
            v-model="jsonResult"
        ></v-textarea>
      <br/>

      </div>



    </v-container>
  </v-container>
</template>

<style>


</style>
